import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CurrencyEnumType } from "@ifgengineering/client-invest-sdk";
import {
  MoneyInputCaption,
  MoneyInputError,
  MoneyInputField,
  MoneyInputWrap,
} from "./MoneyInput.styled";

export interface V3MoneyInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * Required rather than defaulted: a field that assumed sterling put a pound
   * sign on every dollar deal, and the compiler cannot catch a default.
   */
  currency: CurrencyEnumType;
  error?: string;
  caption?: string;
  autoFocus?: boolean;
  max?: number;
  min?: number;
  disabled?: boolean;
  label?: string;
  testID?: string;
}

const LOCALE = "en-GB";

export const V3MoneyInput: FC<V3MoneyInputProps> = ({
  value,
  onChange,
  currency,
  error,
  caption,
  autoFocus,
  max,
  min,
  disabled,
  label = "Amount",
  testID = "v3-money-input",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Errors wait for the investor to finish typing: an amount on its way to
  // valid should not be called wrong mid-keystroke.
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency,
        // en-GB renders USD as "US$" without this; the designs use bare symbols.
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [currency]
  );

  const display = value === null ? "" : formatter.format(value);

  // The figure is reformatted on every keystroke, which invalidates any caret
  // position other than the end of the field.
  useEffect(() => {
    const input = inputRef.current;
    if (!input || document.activeElement !== input) return;
    input.setSelectionRange(input.value.length, input.value.length);
  }, [display]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    if (digits === "") {
      onChange(null);
      return;
    }
    const parsed = Number(digits);
    onChange(max === undefined ? parsed : Math.min(parsed, max));
  };

  const belowMinimum = min !== undefined && value !== null && value < min;
  const showError = Boolean(error) && hasBlurred && !isFocused;
  const invalid = showError || (belowMinimum && hasBlurred && !isFocused);
  const message = showError ? error : caption;
  const messageId = message ? `${testID}-message` : undefined;

  return (
    <MoneyInputWrap>
      <MoneyInputField
        ref={inputRef}
        $invalid={invalid}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        disabled={disabled}
        value={display}
        placeholder={formatter.format(0)}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setHasBlurred(true);
        }}
        aria-label={label}
        aria-invalid={invalid}
        aria-describedby={messageId}
        data-testid={testID}
      />
      {showError ? (
        <MoneyInputError
          id={messageId}
          role="alert"
          data-testid={`${testID}-error`}
        >
          {error}
        </MoneyInputError>
      ) : (
        caption && (
          <MoneyInputCaption id={messageId} data-testid={`${testID}-caption`}>
            {caption}
          </MoneyInputCaption>
        )
      )}
    </MoneyInputWrap>
  );
};
